import React, { useState, useRef, useEffect } from 'react';
import { RecordRTCPromisesHandler } from 'recordrtc';
import { Mic, Video, StopCircle, X, Check, Settings, FlipHorizontal, Play, Pause, Music, RefreshCw } from 'lucide-react';
import { DraggableVideoPreview } from './DraggableVideoPreview';
import { AudioWaveform } from './AudioWaveform'; 

interface TestRecordingModalProps {
  isOpen: boolean;
  onClose: () => void;
  backingTrackUrl?: string;
}

export const TestRecordingModal: React.FC<TestRecordingModalProps> = ({ 
  isOpen, 
  onClose,
  backingTrackUrl
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMode, setRecordingMode] = useState<'audio' | 'video'>('audio');
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [backingTrackOffset, setBackingTrackOffset] = useState(0); // 0ms offset by default
  const [showCamera, setShowCamera] = useState(false);
  const [useBackgroundBlur, setUseBackgroundBlur] = useState(false);
  const [videoMirrored, setVideoMirrored] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recordingVolume, setRecordingVolume] = useState(1.0); // Default to 100%
  
  // Backing audio control
  const [isBackingTrackPlaying, setIsBackingTrackPlaying] = useState(false);
  const [backingTrackVolume, setBackingTrackVolume] = useState(0.7);
  
  const recorderRef = useRef<RecordRTCPromisesHandler | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    return () => {
      // Clean up on component unmount
      stopAllStreams();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
      }
    };
  }, [recordingUrl]);

  const stopAllStreams = () => {
    // Stop recording stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop video preview stream
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }

    // Clear video element source
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  const toggleCamera = async () => {
    try {
      if (showCamera) {
        // Stop camera
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(track => track.stop());
          videoStreamRef.current = null;
        }
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
        setShowCamera(false);
      } else {
        // Start camera
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user'
          }
        });
        
        videoStreamRef.current = stream;
        
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
          videoPreviewRef.current.play().catch(err => {
            console.error('Error playing video:', err);
            setError('Failed to start video preview');
          });
        }
        setShowCamera(true);
      }
    } catch (error) {
      console.error('Error toggling camera:', error);
      setError('Failed to access camera. Please check your device permissions.');
      setShowCamera(false);
    }
  };

  const startRecording = async (mode: 'audio' | 'video') => {
    try {
      setError(null);
      setSuccess(null);
      setRecordingUrl(null);
      setRecordingMode(mode);
      const constraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        },
        video: mode === 'video' ? {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } : false
      };

      let stream: MediaStream;
      
      if (mode === 'video' && videoStreamRef.current) {
        const audioStream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          } 
        });
        stream = new MediaStream([...videoStreamRef.current.getTracks(), ...audioStream.getTracks()]);
      } else {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      }

      streamRef.current = stream;

      const recorder = new RecordRTCPromisesHandler(stream, {
        type: mode === 'audio' ? 'audio' : 'video',
        mimeType: mode === 'audio' ? 'audio/webm;codecs=opus' : 'video/webm;codecs=vp8,opus',
        disableLogs: true,
        numberOfAudioChannels: 2,
        desiredSampRate: 44100,
        audioBitsPerSecond: 128000,
        ...(mode === 'video' && {
          frameInterval: 1,
          quality: 'high'
        })
      });

      recorderRef.current = recorder;
      await recorder.startRecording();
      
      // Start backing track if it's not already playing
      if (backingTrackUrl && audioRef.current && !isBackingTrackPlaying) {
        try {
          await audioRef.current.play();
          setIsBackingTrackPlaying(true);
        } catch (err) {
          console.error('Error playing backing track:', err);
          // Don't set error here as it would interrupt the recording flow
        }
      }
      
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check your device permissions.');
    }
  };

  const stopRecording = async () => {
    if (!recorderRef.current || !streamRef.current) {
      return;
    }

    try {
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Stop recording
      await recorderRef.current.stopRecording();
      
      // Get the recorded blob
      const blob = await recorderRef.current.getBlob();
      
      // Create URL for preview
      const url = URL.createObjectURL(blob);
      setRecordingUrl(url);
      
      // Stop all tracks
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      
      // Reset recording state
      setIsRecording(false);
      setSuccess('Test recording completed successfully! Click play to listen.');
      
      // Note: We're not stopping the backing track here so it can continue playing
      // for testing purposes with the recording playback
    } catch (error) {
      console.error('Error stopping recording:', error);
      setError('Failed to save recording.');
      setIsRecording(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleBackingTrack = () => {
    if (!audioRef.current || !backingTrackUrl) return;
    
    try {
      if (isBackingTrackPlaying) {
        audioRef.current.pause();
        setIsBackingTrackPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setIsBackingTrackPlaying(true);
        }).catch(err => {
          console.error('Error playing backing track:', err);
          setError('Failed to play backing track. Please try again.');
        });
      }
    } catch (err) {
      console.error('Error toggling backing track:', err);
      setError('Failed to control backing track. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-kod-purple-800 rounded-lg w-full max-w-5xl mx-auto">
        <div className="flex items-center justify-between p-4 border-b border-kod-purple-600">
          <div>
            <h3 className="text-xl font-bold">Test Recording</h3>
            <p className="text-white/70 text-sm">
              Test your microphone and camera before recording
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-kod-purple-700 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-start">
              <div className="text-red-500 mr-3 mt-0.5">⚠️</div>
              <p className="text-red-500">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 flex items-start">
              <Check className="text-green-500 mr-3 mt-0.5" size={18} />
              <p className="text-green-500">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Camera */}
            <div>
              <div className="bg-kod-purple-700/50 rounded-lg p-4 mb-4">              
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Camera Preview</h4>
                  
                  <button
                    onClick={toggleCamera}
                    disabled={isRecording}
                    className={`p-2 rounded-lg transition-colors ${
                      isRecording
                        ? 'bg-kod-purple-700/50 text-white/50 cursor-not-allowed'
                        : showCamera
                          ? 'bg-kod-accent text-white hover:bg-kod-accent-dark'
                          : 'bg-kod-purple-700 hover:bg-kod-purple-600 text-white'
                    }`}
                  >
                    <Video size={18} />
                  </button>
                </div>
                
                {showCamera ? (
                  <div className="aspect-video bg-kod-purple-900/50 rounded-lg overflow-hidden">
                    <video 
                      ref={videoPreviewRef}
                      muted 
                      className="w-full h-full object-cover"
                      style={{ transform: 'scaleX(-1)' }}
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-kod-purple-900/50 rounded-lg flex items-center justify-center">
                    <div className="text-center text-white/50">
                      <Video size={32} className="mx-auto mb-2 opacity-50" />
                      <p>Camera is turned off</p>
                      <p className="text-sm mt-1">Click the camera button to enable preview</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Backing Track Player */}
            {backingTrackUrl && (
              <div className="bg-kod-purple-700/50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Backing Track</h4>
                </div>
                
                <div className="bg-kod-purple-800 rounded-lg p-3">
                  <div className="flex items-center mb-3">
                    <Music size={18} className="text-kod-accent mr-2" />
                    <span className="font-medium">Test Backing Track</span>
                  </div>
                  
                  <div className="flex items-center">
                    <button
                      onClick={toggleBackingTrack}
                      className={`flex items-center px-4 py-2 rounded-lg ${
                        isBackingTrackPlaying 
                          ? 'bg-kod-accent text-white hover:bg-kod-accent-dark' 
                          : 'bg-kod-purple-700 hover:bg-kod-purple-600 text-white'
                      }`}
                    >
                      {isBackingTrackPlaying ? (
                        <>
                          <Pause size={18} className="mr-2" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play size={18} className="mr-2" />
                          Play
                        </>
                      )}
                    </button>
                    
                    <div className="ml-3 text-sm text-white/70">
                      {isBackingTrackPlaying ? "Playing..." : "Ready to play"}
                    </div>
                    
                    {isBackingTrackPlaying && (
                      <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-white/70">Volume:</span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="10"
                          value={backingTrackVolume * 100}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) / 100;
                            setBackingTrackVolume(value);
                            if (audioRef.current) {
                              audioRef.current.volume = Math.min(Math.max(value, 0), 1);
                            }
                          }}
                          className="w-24 h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                        />
                        <span className="text-xs text-white/60 w-8">{Math.round(backingTrackVolume * 100)}%</span>
                      </div>
                    )}
                  </div>
                  
                  <audio 
                    ref={audioRef} 
                    src={backingTrackUrl} 
                    playbackRate={1.0}
                    preload="auto"
                    onEnded={() => setIsBackingTrackPlaying(false)}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            )}

            {/* Right Column - Recording Controls & Preview */}
            <div>
              {/* Recording Controls */}
              <div className="bg-kod-purple-700/50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Recording Controls</h4>
                  
                  {isRecording && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse mr-1"></div>
                        <span className="text-white/70 text-sm">Recording</span>
                      </div>
                      <span className="text-white/70">{formatTime(recordingTime)}</span>
                    </div>
                  )}
                </div>
                
                {!isRecording ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => startRecording('audio')}
                      disabled={isRecording}
                      className={`px-4 py-2 rounded-lg flex items-center justify-center ${
                        isRecording
                            ? 'bg-kod-purple-700/50 text-white/50 cursor-not-allowed'
                            : 'bg-kod-purple-700 hover:bg-kod-purple-600 text-white'
                      }`}
                    >
                      <Mic size={18} className="mr-2" />
                      Record Audio Only
                    </button>
                    
                    <button
                      onClick={() => startRecording('video')}
                      disabled={isRecording || !showCamera}
                      className={`px-4 py-2 rounded-lg flex items-center justify-center ${
                        isRecording || !showCamera
                            ? 'bg-kod-purple-700/50 text-white/50 cursor-not-allowed'
                            : 'bg-kod-purple-700 hover:bg-kod-purple-600 text-white'
                      }`}
                    >
                      <Video size={18} className="mr-2" />
                      Record Video
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        stopRecording();
                      }}
                      className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                    >
                      <StopCircle size={18} />
                    </button>
                  </div>
                )}
                
                {!isRecording && !recordingUrl && (
                  <div className="mt-4 text-center text-white/50 text-sm">
                    Test your microphone and camera before recording
                  </div>
                )}
              </div>

              {/* Recording Preview */}
              {recordingUrl && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Test Recording Preview</h4>
                  </div>
                  <div className="bg-kod-purple-700/50 rounded-lg overflow-hidden">
                    {recordingMode === 'audio' ? (
                      <div className="p-4">
                        <AudioWaveform 
                          url={recordingUrl} 
                          tempo={1.0}
                          pitch={0}
                          volume={Math.min(Math.max(recordingVolume, 0), 1)}
                        />
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/70">Recording Volume</span>
                            <div className="flex items-center gap-2">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                step="10"
                                value={recordingVolume * 100}
                                onChange={(e) => setRecordingVolume(parseFloat(e.target.value) / 100)}
                                className="w-32 h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                              />
                              <span className="text-xs text-white/60 w-12">{Math.round(recordingVolume * 100)}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-black">
                        <video
                          src={recordingUrl}
                          volume={Math.min(Math.max(recordingVolume, 0), 1)}
                          preload="auto"
                          playbackRate={1.0}
                          controls
                          className="w-full h-full"
                          onPlay={() => {
                            if (audioRef.current) {
                              // Set the current time with the delay offset
                              const offsetInSeconds = backingTrackOffset / 1000;
                              const videoElement = document.querySelector('video');
                              if (videoElement) {
                                audioRef.current.currentTime = Math.max(0, videoElement.currentTime - offsetInSeconds);
                                
                                // Play with a slight delay to ensure synchronization
                                setTimeout(() => {
                                  if (audioRef.current) {
                                    audioRef.current.play().catch(err => {
                                      console.error('Error playing backing track:', err);
                                    });
                                  }
                                }, 10);
                              }
                            }
                          }}
                          onPause={() => {
                            if (audioRef.current) {
                              audioRef.current.pause();
                            }
                          }}
                          onVolumeChange={(e) => setRecordingVolume((e.target as HTMLVideoElement).volume)}
                        />
                      </div>
                    )}
                    
                    {backingTrackUrl && (
                      <div className="p-4 border-t border-kod-purple-600/50 mt-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-white/70">Backing Track Volume</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="10"
                              value={backingTrackVolume * 100}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) / 100;
                                setBackingTrackVolume(value);
                                if (audioRef.current) {
                                  audioRef.current.volume = Math.min(Math.max(value, 0), 1);
                                }
                              }}
                              className="w-32 h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                            />
                            <span className="text-xs text-white/60 w-12">{Math.round(backingTrackVolume * 100)}%</span>
                          </div>
                        </div>
                        
                        {/* Playback Sync Control */}
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-sm text-white/70">Backing Track Timing</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="-3000"
                              max="3000"
                              step="100"
                              value={backingTrackOffset}
                              onChange={(e) => setBackingTrackOffset(parseInt(e.target.value))}
                              className="w-32 h-2 bg-kod-purple-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-kod-accent"
                            />
                            <span className="text-xs text-white/60 w-20">{backingTrackOffset > 0 ? `+${backingTrackOffset}` : backingTrackOffset}ms</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setRecordingUrl(null);
                    setSuccess(null);
                    setError(null);
                  }}
                  className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors flex items-center"
                  disabled={!recordingUrl && !isRecording}
                >
                  <RefreshCw size={18} className="mr-2" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end gap-3 p-4 border-t border-kod-purple-600">
          <button
            onClick={() => {
              // Stop audio before closing
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
              onClose();
            }}
            className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};